// The app's data, generated from .appfactory/product-plan.json.
//
// Regenerated by `appfactory product apply`. Edit the PLAN and re-apply
// rather than editing here, or the next apply will disagree with you.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch(() => []);   // no IndexedDB (SSR, private mode) — stay in memory

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

/** The one place the app forecasts for, plus its alert preferences and permission state. The app holds exactly one record. */
export const locations = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededLocation = demoData([
  { id: -1, name: 'Location 1', postcode: 'Sample postcode', latitude: 1, longitude: 1, detectedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), rainThresholdMm: 1, warningsEnabled: true, permissionState: 'unasked', pushToken: 'Sample pushToken', pushTokenUpdatedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, name: 'Location 2', postcode: 'Sample postcode', latitude: 2, longitude: 2, detectedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), rainThresholdMm: 2, warningsEnabled: false, permissionState: 'granted', pushToken: 'Sample pushToken', pushTokenUpdatedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, name: 'Location 3', postcode: 'Sample postcode', latitude: 3, longitude: 3, detectedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), rainThresholdMm: 3, warningsEnabled: false, permissionState: 'denied', pushToken: 'Sample pushToken', pushTokenUpdatedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededLocation, (rows) => {
  if (rows.length && !locations.value.length) locations.value = [...rows];
}, { immediate: true });

export const locationsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'location');
  if (mine.length) locations.value = mine;
  return locations.value;
});

export function addLocation(input = {}) {
  const row = { id: nextId(), entity: 'location',
    name: input.name ?? '',
    postcode: input.postcode ?? '',
    latitude: input.latitude ?? 0,
    longitude: input.longitude ?? 0,
    detectedAt: input.detectedAt ?? new Date().toISOString(),
    rainThresholdMm: input.rainThresholdMm ?? 0,
    warningsEnabled: input.warningsEnabled ?? false,
    permissionState: input.permissionState ?? '',
    pushToken: input.pushToken ?? '',
    pushTokenUpdatedAt: input.pushTokenUpdatedAt ?? new Date().toISOString(),
  };
  locations.value = [row, ...locations.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveLocation(row) {
  locations.value = locations.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeLocation(id) {
  locations.value = locations.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findLocation = (id) =>
  locations.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentLocation = computed(() => locations.value.slice(0, 3));
export const locationsTitle = (row) => row?.name;

/** The current conditions reading for the location. */
export const observations = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededObservation = demoData([
  { id: -1, location: -1, recordedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), tempC: 1, feelsLikeC: 1, windKph: 1, windDirection: 'n', rainSince9amMm: 1 },
  { id: -2, location: -2, recordedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), tempC: 2, feelsLikeC: 2, windKph: 2, windDirection: 'ne', rainSince9amMm: 2 },
  { id: -3, location: -3, recordedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), tempC: 3, feelsLikeC: 3, windKph: 3, windDirection: 'e', rainSince9amMm: 3 },
]);
watch(seededObservation, (rows) => {
  if (rows.length && !observations.value.length) observations.value = [...rows];
}, { immediate: true });

export const observationsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'observation');
  if (mine.length) observations.value = mine;
  return observations.value;
});

export function addObservation(input = {}) {
  const row = { id: nextId(), entity: 'observation',
    location: input.location ?? '',
    recordedAt: input.recordedAt ?? new Date().toISOString(),
    tempC: input.tempC ?? 0,
    feelsLikeC: input.feelsLikeC ?? 0,
    windKph: input.windKph ?? 0,
    windDirection: input.windDirection ?? '',
    rainSince9amMm: input.rainSince9amMm ?? 0,
  };
  observations.value = [row, ...observations.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveObservation(row) {
  observations.value = observations.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeObservation(id) {
  observations.value = observations.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findObservation = (id) =>
  observations.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentObservation = computed(() => observations.value.slice(0, 3));
export const observationsTitle = (row) => row?.id;

/** One hour of the twelve-hour rain trace: chance drives the polyline, mm drives the fill alpha. */
export const forecastHours = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededForecastHour = demoData([
  { id: -1, location: -1, time: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), chance: 1, mm: 1 },
  { id: -2, location: -2, time: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), chance: 2, mm: 2 },
  { id: -3, location: -3, time: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), chance: 3, mm: 3 },
]);
watch(seededForecastHour, (rows) => {
  if (rows.length && !forecastHours.value.length) forecastHours.value = [...rows];
}, { immediate: true });

export const forecastHoursReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'forecast-hour');
  if (mine.length) forecastHours.value = mine;
  return forecastHours.value;
});

export function addForecastHour(input = {}) {
  const row = { id: nextId(), entity: 'forecast-hour',
    location: input.location ?? '',
    time: input.time ?? new Date().toISOString(),
    chance: input.chance ?? 0,
    mm: input.mm ?? 0,
  };
  forecastHours.value = [row, ...forecastHours.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveForecastHour(row) {
  forecastHours.value = forecastHours.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeForecastHour(id) {
  forecastHours.value = forecastHours.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findForecastHour = (id) =>
  forecastHours.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentForecastHour = computed(() => forecastHours.value.slice(0, 3));
export const forecastHoursTitle = (row) => row?.id;

/** One day of the seven-day outlook. */
export const forecastDays = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededForecastDay = demoData([
  { id: -1, location: -1, date: '2026-01-01', minC: 1, maxC: 1, chance: 1, mmLow: 1, mmHigh: 1, summary: 'Sample summary' },
  { id: -2, location: -2, date: '2026-01-02', minC: 2, maxC: 2, chance: 2, mmLow: 2, mmHigh: 2, summary: 'Sample summary' },
  { id: -3, location: -3, date: '2026-01-03', minC: 3, maxC: 3, chance: 3, mmLow: 3, mmHigh: 3, summary: 'Sample summary' },
]);
watch(seededForecastDay, (rows) => {
  if (rows.length && !forecastDays.value.length) forecastDays.value = [...rows];
}, { immediate: true });

export const forecastDaysReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'forecast-day');
  if (mine.length) forecastDays.value = mine;
  return forecastDays.value;
});

export function addForecastDay(input = {}) {
  const row = { id: nextId(), entity: 'forecast-day',
    location: input.location ?? '',
    date: input.date ?? new Date().toISOString(),
    minC: input.minC ?? 0,
    maxC: input.maxC ?? 0,
    chance: input.chance ?? 0,
    mmLow: input.mmLow ?? 0,
    mmHigh: input.mmHigh ?? 0,
    summary: input.summary ?? '',
  };
  forecastDays.value = [row, ...forecastDays.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveForecastDay(row) {
  forecastDays.value = forecastDays.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeForecastDay(id) {
  forecastDays.value = forecastDays.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findForecastDay = (id) =>
  forecastDays.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentForecastDay = computed(() => forecastDays.value.slice(0, 3));
export const forecastDaysTitle = (row) => row?.summary;

/** A severe weather warning in force for the location. */
export const weatherWarnings = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededWeatherWarning = demoData([
  { id: -1, location: -1, headline: 'Sample headline', severity: 'minor', body: 'A short body for Weather warning 1, long enough to show wrapping.', issuedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), expiresAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, location: -2, headline: 'Sample headline', severity: 'moderate', body: 'A short body for Weather warning 2, long enough to show wrapping.', issuedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), expiresAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, location: -3, headline: 'Sample headline', severity: 'severe', body: 'A short body for Weather warning 3, long enough to show wrapping.', issuedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), expiresAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededWeatherWarning, (rows) => {
  if (rows.length && !weatherWarnings.value.length) weatherWarnings.value = [...rows];
}, { immediate: true });

export const weatherWarningsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'weather-warning');
  if (mine.length) weatherWarnings.value = mine;
  return weatherWarnings.value;
});

export function addWeatherWarning(input = {}) {
  const row = { id: nextId(), entity: 'weather-warning',
    location: input.location ?? '',
    headline: input.headline ?? '',
    severity: input.severity ?? '',
    body: input.body ?? '',
    issuedAt: input.issuedAt ?? new Date().toISOString(),
    expiresAt: input.expiresAt ?? new Date().toISOString(),
  };
  weatherWarnings.value = [row, ...weatherWarnings.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveWeatherWarning(row) {
  weatherWarnings.value = weatherWarnings.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeWeatherWarning(id) {
  weatherWarnings.value = weatherWarnings.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findWeatherWarning = (id) =>
  weatherWarnings.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentWeatherWarning = computed(() => weatherWarnings.value.slice(0, 3));
export const weatherWarningsTitle = (row) => row?.headline;
